<template>
	<DashboardItem :header="$t('dashboard.userPulseLogs.header')" :timing="$t('dashboard.userPulseLogs.timing')">
		<Row>
			<Col :span="12">
				<VerticalLayout>
					<LabeledSwitch
						v-model="enabled"
						:small="true"
						:rightLabel="$t('dashboard.userPulseLogs.enabled')"
					/>
					<LabeledSwitch
						v-model="autoScroll"
						:disabled="!enabled"
						:small="true"
						:rightLabel="$t('dashboard.userPulseLogs.autoScroll')"
					/>
					<LabeledSwitch
						v-model="parseItems"
						:small="true"
						:rightLabel="$t('dashboard.userPulseLogs.parseItems')"
					/>
				</VerticalLayout>
			</Col>
			<Col :span="12">
				<VerticalLayout>
					<Select
						size="small"
						class="width-100"
						v-model:value="selectedUsernameAddress"
						:options="usernameAdresses"
						:allowClear="true"
						:placeholder="$t('dashboard.userPulseLogs.usernameAddressFilter')"
						@change="onUsernameAddressChange"
						:getPopupContainer="getPopupContainer"
						show-search
					/>
					<Select
						size="small"
						class="width-100"
						v-model:value="selectedUsername"
						:options="usernames"
						:allowClear="true"
						:placeholder="$t('dashboard.userPulseLogs.usernameFilter')"
						@change="onUsernameChange"
						:getPopupContainer="getPopupContainer"
						show-search
					/>
				</VerticalLayout>
			</Col>
		</Row>
		<Row>
			<Col :span="24">
				<div v-bind="containerProps" class="log-container pd-vertical">
					<div v-bind="wrapperProps" v-if="list.length > 0">
						<UserPulseLogItem v-for="item in list" :key="item.id" :item="item" :parseItems="parseItems" />
					</div>
					<div v-else>
						<Empty :image="simpleImage" :description="$t('dashboard.userPulseLogs.noEvents')" />
					</div>
				</div>
				<div>
					<Badge :status="statusBadge" :text="$t('dashboard.userPulseLogs.websocketStatus.' + statusBadge)" /><br>
				</div>
			</Col>
		</Row>
	</DashboardItem>
</template>

<script>
import { ref, computed, watch, onMounted } from 'vue';
import { Col, Row, Badge, Select, Empty } from 'ant-design-vue';

import { useWebSocket, useVirtualList } from '@vueuse/core';

import Api from '@/api/Api';

import { BASE_DOMAIN } from '@/settings';
import { useUserStore } from '@/stores/user';

import { usePopupContainer } from '@/utils/usePopupContainer';

import DashboardItem from '@/components/dashboard/DashboardItem.vue';
import UserPulseLogItem from '@/components/dashboard/userPulse/UserPulseLogItem.vue';

import LabeledSwitch from '@/components/common/LabeledSwitch.vue';
import VerticalLayout from '@/components/common/VerticalLayout.vue';

const MAX_BUFFER_SIZE = 20000;	


export default {
	components: {
		Row, Col, Badge, Select, Empty,
		LabeledSwitch, VerticalLayout,
		DashboardItem, UserPulseLogItem,
	},
	setup() {
		const enabled = ref(true);
		const autoScroll = ref(true);
		const parseItems = ref(true);
		const buffer = ref([]);
		const userStore = useUserStore();
		const getWsUrl = () => {
			if (BASE_DOMAIN) {
				return `//${BASE_DOMAIN}/dashboard/caster/user/logs/?token=${userStore.token}`;
			}
			const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
			const wsUrl = `${protocol}://${window.location.host}/dashboard/caster/user/logs/?token=${userStore.token}`;
			return wsUrl;
		}
		const { status, data } = useWebSocket(getWsUrl());
		const statusBadge = computed(() => ({
			'CONNECTING': 'processing',
			'OPEN': 'success',
			'CLOSED': 'error',
		}[status.value]));
		watch(data, (newData) => {
			if (newData != null && enabled.value) {
				buffer.value = [...buffer.value, JSON.parse(newData)];
				if (buffer.value.length > MAX_BUFFER_SIZE) {
					buffer.value = buffer.value.slice(-MAX_BUFFER_SIZE);
				}
				if (autoScroll.value) {
					scrollTo(buffer.value.length - 1);
				}
			}
		}, { immediate: true });

		const usernameAdresses = ref([]);
		const selectedUsernameAddress = ref(null);
		const usernames = ref([]);
		const selectedUsername = ref(null);
		onMounted(() => {
			Api.dashboardUserPulseProfilesListGet(response => {
				usernameAdresses.value = response.profiles.map(profile => ({
					username: profile.username,
					address: profile.address,
					value: profile.username + '|' + profile.address,
					label: profile.username + ' ' + profile.address,
				}));
				const usernamesMap = new Map();
				for (const profile of response.profiles) {
					if (usernamesMap.has(profile.username)) {
						if (usernamesMap.get(profile.username) < profile.last_datetime) {
							usernamesMap.set(profile.username, profile.last_datetime);
						}
					} else {
						usernamesMap.set(profile.username, profile.last_datetime);
					}
				}
				usernames.value = Array.from(usernamesMap, (item) => ({
					value: item[0],
					label: item[0],
				}));
			});
		});

		const onUsernameAddressChange = () => {
			selectedUsername.value = null;
		};
		const onUsernameChange = () => {
			selectedUsernameAddress.value = null;
		};
		const filteredBuffer = computed(() => {
			if (selectedUsernameAddress.value != null) {
				const usernameAddress = usernameAdresses.value.find(item => item.value === selectedUsernameAddress.value);
				return buffer.value.filter(item => item.user === usernameAddress.username && item.addr === usernameAddress.address);
			}
			if (selectedUsername.value != null) {
				return buffer.value.filter(item => item.user === selectedUsername.value);
			}
			return buffer.value;
		});
		const { list, containerProps, wrapperProps, scrollTo } = useVirtualList(filteredBuffer, { itemHeight: 22 });
		const { getPopupContainer } = usePopupContainer();
		return {
			enabled,
			autoScroll,
			parseItems,
			statusBadge,
			usernameAdresses,
			selectedUsernameAddress,
			usernames,
			selectedUsername,
			onUsernameAddressChange,
			onUsernameChange,
			list,
			containerProps,
			wrapperProps,
			getPopupContainer,
			simpleImage: Empty.PRESENTED_IMAGE_SIMPLE,
		};
	},
}
</script>

<style scoped>
.log-container {
	border: 1px solid #d9d9d9;
	padding: 10px;
	height: 420px;
	background: white;
}
</style>
